import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import Dialog from '$tablet-app/src/app/components/Dialog';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { MainDrawerScreenProps } from '$tablet-app/src/app/navigation/MainDrawerNavigator';
import callPhoneNumber from '$tablet-app/src/app/phone/callPhoneNumber/callPhoneNumber';
import messageContact from '$tablet-app/src/app/phone/messageContact/messageContact';
import { SectionIntroScreen } from '$tablet-app/src/app/screens/SectionIntroScreen';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { InfoVideoName } from '$tablet-app/src/app/videos/types';
import { Button } from '@akin-home/buttons/native';
import {
  ContactModel,
  ContactTypeEmojis,
  ContactTypes,
  PhoneNumber,
  sortContactsByName,
} from '@akin-home/contacts';
import { useListContactsForHabitat } from '@akin-home/contacts/react';
import { useHabitatID } from '@akin-home/habitats/react';
import { useGetSystemProperty } from '@akin-home/system-properties-react';
import { Text } from '@akin-home/text/native';
import { useIsFocused } from '@react-navigation/native';
import produce from 'immer';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useSetContactFavourite } from '../../hooks/useSetContactFavourite';
import ContactDetailsModal from '../ContactDetailsModal';
import ContactsScreenView from './ContactsScreenView';

const parameters = {
  contact_id: 'text',
};

interface State {
  editingContact?: ContactModel;
  input: string;
  showingForm: boolean;
  contactToDisplay?: ContactModel;
}

enum Actions {
  SET_INPUT,
  EDIT_CONTACT,
  OPEN_CREATE_FORM,
  CLOSE_FORM,
  SHOW_DETAILS,
  HIDE_DETAILS,
}

type Action =
  | { type: Actions.SET_INPUT; payload: { input: string } }
  | { type: Actions.EDIT_CONTACT; payload: { editingContact: ContactModel } }
  | { type: Actions.OPEN_CREATE_FORM; payload?: undefined }
  | { type: Actions.CLOSE_FORM; payload?: undefined }
  | { type: Actions.SHOW_DETAILS; payload: { contact: ContactModel } }
  | { type: Actions.HIDE_DETAILS };

const reducer = produce((state: State, action: Action) => {
  switch (action.type) {
    case Actions.SET_INPUT:
      state.input = action.payload.input;
      break;
    case Actions.EDIT_CONTACT:
      state.showingForm = true;
      state.editingContact = action.payload.editingContact;
      break;
    case Actions.OPEN_CREATE_FORM:
      state.showingForm = true;
      state.editingContact = undefined;
      break;
    case Actions.CLOSE_FORM:
      state.showingForm = false;
      state.editingContact = undefined;
      break;
    case Actions.SHOW_DETAILS:
      state.contactToDisplay = action.payload.contact;
      break;
    case Actions.HIDE_DETAILS:
      state.contactToDisplay = undefined;
      break;
  }
});

const defaultValues: State = {
  input: '',
  showingForm: false,
};

const ContactsScreen = (props: MainDrawerScreenProps<'Contacts'>) => {
  const {
    navigation: { navigate, setParams },
    route,
  } = props;

  const is_focused = useIsFocused();

  const habitatID = useHabitatID();
  const favourite_contacts = useStateStore(
    (state) => state.current_user?.favouriteContacts
  );

  useUpdateScreenChatContext();

  const toggleFavourite = useSetContactFavourite();

  const { data: info_video } = useGetSystemProperty(
    InfoVideoName.CONTACTS_SCREEN
  );

  const [{ input, contactToDisplay }, dispatch] = useReducer(
    reducer,
    defaultValues
  );

  const { data: contacts, isLoading: loading } =
    useListContactsForHabitat(habitatID);

  useEffect(() => {
    if (route.params?.contact_id && !loading) {
      const contact = contacts.find(
        (contact) => contact.id === route.params?.contact_id
      );

      if (contact) {
        onShowDetails(contact);
      }

      setParams({ contact_id: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.contact_id, loading]);

  const favourites = favourite_contacts || [];

  const isFavourite = (contactId: string): boolean => {
    return favourites.includes(contactId);
  };

  const getFavouriteContacts = (): ContactModel[] => {
    return sorted_contacts.filter((x) => isFavourite(x.id));
  };

  const [filters, setFilters] = useState(default_filters);

  const [isAllSelected, setAllSelected] = useState(true);

  const onSelectAll = () => {
    setAllSelected(!isAllSelected);

    setFilters(
      filters.map(({ label, emoji }) => ({
        label: label,
        emoji: emoji,
        selected: false,
      }))
    );
  };

  const onFilterPress = (filter: string) => {
    if (isAllSelected) {
      setAllSelected(false);
    }

    // Toggle whether filter is selected or not
    const newFilters = filters.map(({ label, emoji, selected }) => ({
      label: label,
      emoji: emoji,
      selected: label === filter ? !selected : selected,
    }));

    setFilters(newFilters);

    // If no filters are selected, default to All
    if (newFilters.every((item) => !item.selected)) {
      setAllSelected(true);
    }
  };

  const setSearchInput = (input: string) => {
    dispatch({ type: Actions.SET_INPUT, payload: { input } });
  };

  const onShowDetails = (contact: ContactModel) =>
    dispatch({ type: Actions.SHOW_DETAILS, payload: { contact } });

  const hideDetails = () => dispatch({ type: Actions.HIDE_DETAILS });

  const onEditClick = (contact: ContactModel) => {
    hideDetails();
    navigate('EditContact', { contact_id: contact.id });
  };

  const onPressAdd = () => navigate('CreateContact');

  const callContact = (contact: ContactModel) => {
    const number = contact.phoneNumbers[0]?.number;

    if (!number) return;

    sendBackgroundMessage(intent_labels.call_contact, {
      context: { contact: { id: contact.id, name: contact.name }, number },
    });

    callPhoneNumber(number);
  };

  const messageContactHandler = (contact: ContactModel) => {
    contact.phoneNumbers.length &&
      messageContact(contact.phoneNumbers[0].number);
  };

  const onPressVideoCall = (contact: ContactModel) =>
    navigate('CreateVideoCall', { contact_id: contact.id });

  const sorted_contacts = useMemo(() => {
    const containsFilterTags = (contact: ContactModel) => {
      if (isAllSelected) {
        return true;
      }

      let result = false;
      const selectedTags = filters
        .filter((item) => item.selected)
        .map((item) => item.label);

      if (selectedTags.includes('Favourite')) {
        result = contact.favourite;
      }

      const intersection = contact.tags.filter((item: string) =>
        selectedTags.includes(item)
      );

      return result || intersection.length > 0;
    };

    return sortContactsByName(
      input === ''
        ? contacts.filter((item) => containsFilterTags(item))
        : contacts.filter(
            (item) =>
              containsFilterTags(item) &&
              (item.name.toLowerCase().includes(input.toLowerCase()) ||
                containsPhoneNumber(item.phoneNumbers, input) ||
                containsEmailAddress(item.emailAddresses, input) ||
                containsNotes(item.notes, input))
          )
    );
  }, [input, contacts, filters, isAllSelected]);

  useEffect(() => {
    if (is_focused && !loading) {
      sendBackgroundMessage(intent_labels.open_contacts_screen);
    }
  }, [is_focused, loading]);

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!loading && !contacts.length && info_video?.value?.length) {
    return (
      <SectionIntroScreen
        screen_title="Contacts"
        video_title="Watch the video and learn how to add your first contact"
        source={{ drive_id: info_video.value }}
        action_button={
          <Button icon="plus" onPress={onPressAdd}>
            Add your first contact
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ContactsScreenView
        favourites={getFavouriteContacts()}
        contacts={sorted_contacts}
        filters={filters}
        search_input={input}
        isAllSelected={isAllSelected}
        onSelectAll={onSelectAll}
        onFilterPress={onFilterPress}
        onSubmitSearch={setSearchInput}
        onPressViewContact={onShowDetails}
        onPressCallContact={callContact}
        onPressMessageContact={messageContactHandler}
        onPressFavourite={toggleFavourite}
        onPressAdd={onPressAdd}
        onPressVideoCallContact={onPressVideoCall}
      />
      <DeveloperScreenInfo
        parameters={<Text>{JSON.stringify(parameters, null, 3)}</Text>}
      />
      <Dialog visible={!!contactToDisplay} onClose={hideDetails}>
        {contactToDisplay ? (
          <ContactDetailsModal
            contact={contactToDisplay}
            isFavourite={isFavourite(contactToDisplay.id)}
            onClose={hideDetails}
            onEdit={onEditClick}
            onCallPhone={callPhoneNumber}
            onPressMessageContact={messageContactHandler}
            onFavourite={toggleFavourite}
            onPressVideoCall={onPressVideoCall}
          />
        ) : null}
      </Dialog>
    </>
  );
};

const containsPhoneNumber = (phoneNumbers: PhoneNumber[], key: string) =>
  phoneNumbers.filter((item: PhoneNumber) => item.number?.includes(key))
    .length > 0;

const containsEmailAddress = (emailAddresses: string[], key: string) =>
  emailAddresses.filter((item: string) =>
    item.toLowerCase().includes(key.toLowerCase())
  ).length > 0;

const containsNotes = (notes: string[], key: string) =>
  notes.filter((item: string) => item.toLowerCase().includes(key.toLowerCase()))
    .length > 0;

const default_filters = [
  {
    label: ContactTypes.Family,
    emoji: ContactTypeEmojis.Family,
    selected: false,
  },
  {
    label: ContactTypes.Friend,
    emoji: ContactTypeEmojis.Friend,
    selected: false,
  },
  {
    label: ContactTypes.Neighbour,
    emoji: ContactTypeEmojis.Neighbour,
    selected: false,
  },
  {
    label: ContactTypes.Support,
    emoji: ContactTypeEmojis.Support,
    selected: false,
  },
  {
    label: ContactTypes.Work,
    emoji: ContactTypeEmojis.Work,
    selected: false,
  },
  {
    label: ContactTypes.School,
    emoji: ContactTypeEmojis.School,
    selected: false,
  },
  {
    label: ContactTypes.Emergency,
    emoji: ContactTypeEmojis.Emergency,
    selected: false,
  },
];

export default ContactsScreen;
